<template>
  <div v-if="visible" class="dismissible-banner" :class="`dismissible-banner--${variant}`">
    <div class="dismissible-banner__content">
      <slot />
    </div>
    <button class="dismissible-banner__close" @click="dismiss" :aria-label="$t('common.close')">
      <span class="material-icons">close</span>
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const props = defineProps({
  cookieKey: {
    type: String,
    required: true,
  },
  cookieDays: {
    type: Number,
    default: 30,
  },
  variant: {
    type: String,
    default: 'info',
    validator: (v) => ['info', 'success', 'warning', 'danger'].includes(v),
  },
})

const emit = defineEmits(['close'])

const visible = ref(false)

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=1; expires=${expires}; path=/`
}

function dismiss() {
  visible.value = false
  setCookie(props.cookieKey, props.cookieDays)
  emit('close')
}

onMounted(() => {
  if (!getCookie(props.cookieKey)) {
    visible.value = true
  }
})
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.dismissible-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border-left: 4px solid transparent;
}

.dismissible-banner--info {
  background-color: #e3f4f3;
  border-left-color: #3caea3;
  color: #002e5f;
}

.dismissible-banner--success {
  background-color: #efffe8;
  border-left-color: #2a9187;
  color: #002e5f;
}

.dismissible-banner--warning {
  background-color: #fffcf4;
  border-left-color: #fbb83e;
  color: #002e5f;
}

.dismissible-banner--danger {
  background-color: #fee2e2;
  border-left-color: #d60812;
  color: #002e5f;
}

.dismissible-banner--promo {
  width: 96%;
  background:
    url("@/assets/img/webp/banner.webp") right no-repeat,
    transparent linear-gradient(89deg, $bluTwo 0%, $bluThree 100%) 0% 0% no-repeat padding-box;
  border-radius: 22px;
  border-left: none;
  padding: 40px 20px 20px 20px;
  margin-bottom: 45px;
  color: $grayFour;
  font-family: OpenSansRegular;
  font-size: 1rem;

  .dismissible-banner__content {
    max-width: 75%;
    padding-left: 20px;
    font-size: 1.0625rem;
  }

  .dismissible-banner__close {
    width: 25px;
    height: 25px;
    min-width: 25px;
    border-radius: 50px;
    background-color: $bluFive;
    padding: 3px;
    color: white;
  }
}

.dismissible-banner__content {
  flex: 1;
  min-width: 0;
  line-height: 1.8;
  font-family: OpenSansRegular;
}

.dismissible-banner__close {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 2px;
  flex-shrink: 0;
  opacity: 0.6;
  color: inherit;
}

.dismissible-banner__close:hover {
  opacity: 1;
}

.dismissible-banner__close .material-icons {
  font-family: 'Material Icons';
  font-size: 18px;
}
</style>